import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import MasterAdminMaintenancePreview from '../components/MasterAdminMaintenancePreview';
import { masterAdminApi } from '../services/api';
import { mintDemoCreationCode, PROTECTED_SANDBOX_ORG_TOKEN } from '../lib/keyRegistry';
import { openMailDraftWindow, fillGmailDraft, describeGmailDraftResult } from '../lib/openMailDraft';
import {
  isMasterAdminGateOpen,
  isMasterTestingSessionActive,
  openMasterAdminGate,
} from '../lib/masterTestingKey';
import { useAuth } from '../context/AuthContext';
import './OrgAdmin.css';
import './SystemAdmin.css';
import './PortalLogin.css';

type KnowledgeTab = 'baselines' | 'operational';

type SubscriptionState = 'Active' | 'Trialing' | 'Lapsed' | 'Field Test';

interface PrivateMenteeRow {
  menteeKey: string;
  dateEnrolled: string;
  menteeName: string;
  phone: string;
  email: string;
  locationAddress: string;
  subscriptionState: SubscriptionState;
  timeLeft: string;
}

type OrgSubscriptionState = 'Package Active' | 'Provisioned' | 'Lapsed';

interface OrganisationRow {
  organisationKey: string;
  dateEnrolled: string;
  organisationName: string;
  contactName: string;
  phone: string;
  email: string;
  subscriptionState: OrgSubscriptionState;
  timeLeft: string;
}

const KNOWLEDGE_BASELINES = [
  {
    folderName: 'Classroom-Learning-Material',
    contextMap: 'Pedagogy & curriculum intelligence',
    status: 'Active / Indexed',
  },
  {
    folderName: 'My Thesis',
    contextMap: 'Doctoral research corpus',
    status: 'Active / Indexed',
  },
  {
    folderName: 'Action Research Action Learning',
    contextMap: 'ARAL methodology frameworks',
    status: 'Active / Indexed',
  },
];

const OPERATIONAL_TOPICS = [
  {
    title: 'Workplace Development',
    folders: ['Business Research', 'Management Material'],
  },
  {
    title: 'Mentoring Dynamics',
    folders: [
      'Mentology Framework Pack (Tier 1 IP)',
      'Mentor_ARCHIVE Reference Only',
      'Mentoring Dynamics Development Pack',
    ],
  },
];

type MasterAdminSidebarItem =
  | { kind: 'section'; label: string }
  | { kind: 'external'; label: string; href: string };

const MASTER_ADMIN_SIDEBAR_NAV: MasterAdminSidebarItem[] = [
  { kind: 'section', label: 'Global Knowledge Hub' },
  { kind: 'section', label: 'Window A (Private)' },
  { kind: 'section', label: 'Window B (Org)' },
  { kind: 'section', label: 'Demo Key Generator' },
  { kind: 'section', label: 'System Data' },
  { kind: 'section', label: 'Maintenance Preview' },
  { kind: 'external', label: 'Stripe Dashboard', href: 'https://dashboard.stripe.com' },
];

type SupportStatus = 'Open — Awaiting Review' | 'In Progress' | 'Resolved';

interface PrivateIncidentRow {
  id: string;
  menteeKey: string;
  timestamp: string;
  alertSource: string;
  incidentStatus: string;
  systemResponse: string;
  resolved: boolean;
  telemetry: string;
}

interface CorporateSupportRow {
  organisationKey: string;
  organisationName: string;
  contactName: string;
  status: SupportStatus;
  supportDocument: string;
  ticketId?: number;
}

const SUPPORT_STATUS_OPTIONS: SupportStatus[] = [
  'Open — Awaiting Review',
  'In Progress',
  'Resolved',
];

type DemoKeyTab = 'individual' | 'organisation';

type DemoKeyLedgerType = 'Individual' | 'Org';

type DemoKeyLedgerStatus = 'Active' | 'Not Used';

interface DemoKeyLedgerRow {
  id: string;
  demoCode: string;
  type: DemoKeyLedgerType;
  status: DemoKeyLedgerStatus;
  registeredEmail: string;
  record: string[];
}

const DELETE_CONFIRM_MESSAGE = 'Are you sure you want to permanently delete this entry?';

function mapDemoCodeRow(row: {
  id: number;
  code: string;
  track: string;
  status: string;
  registered_email?: string | null;
  registeredEmail?: string | null;
  ledger_record_json?: string | null;
}): DemoKeyLedgerRow {
  let record: string[] = [];
  if (row.ledger_record_json) {
    try {
      record = JSON.parse(row.ledger_record_json);
    } catch {
      record = [];
    }
  }
  return {
    id: String(row.id),
    demoCode: row.code,
    type: row.track === 'ELT' ? 'Org' : 'Individual',
    status: row.status === 'used' ? 'Used' : 'Not Used',
    registeredEmail: row.registered_email || row.registeredEmail || '—',
    record,
  };
}

function MasterAdminLock({
  onUnlock,
  onReject,
}: {
  onUnlock: (key: string) => Promise<{ masterTestingOverride?: boolean; user?: { masterTestingOverride?: boolean } }>;
  onReject: () => Promise<void>;
}) {
  const [key, setKey] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = key.trim();
    if (!trimmed) {
      setError('Please enter your master access key.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const data = await onUnlock(trimmed);
      if (!data.masterTestingOverride && !data.user?.masterTestingOverride) {
        await onReject();
        setError('This key does not unlock Master Admin.');
      }
    } catch (err: unknown) {
      setError((err as { error?: string }).error || 'Access denied.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="portal-login-screen">
      <div className="portal-login-logo-wrap">
        <img
          src={encodeURI('/Assets/logo/Mentology Logo.png')}
          alt="Mentology"
          className="portal-login-logo brand-logo"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      </div>
      <div className="portal-login-content">
        <h1 className="section-header portal-login-heading">Master Admin Access</h1>
        <p className="master-admin-lock-copy">This console is private. Enter your master access key to continue.</p>
        <form onSubmit={handleSubmit} className="portal-login-form" noValidate>
          <input
            className="input-field portal-login-input"
            type="password"
            name="masterAccessKey"
            autoComplete="current-password"
            spellCheck={false}
            placeholder="Enter master access key"
            value={key}
            onChange={(e) => {
              setKey(e.target.value);
              if (error) setError('');
            }}
            aria-label="Master access key"
          />
          {error && <p className="error-text portal-login-error">{error}</p>}
          <div className="button-control-group portal-login-actions">
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Unlocking...' : 'UNLOCK CONSOLE'}
            </button>
            <Link to="/" className="btn-primary">Back to Home</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function SystemAdmin() {
  const { login, logout, user, masterSandboxUnlocked } = useAuth();
  const [gateOpen, setGateOpen] = useState(
    () => isMasterAdminGateOpen() || isMasterTestingSessionActive(),
  );
  const [activeSection, setActiveSection] = useState('Global Knowledge Hub');
  const [knowledgeHubTab, setKnowledgeHubTab] = useState<KnowledgeTab>('baselines');
  const [privateMenteeRows, setPrivateMenteeRows] = useState<PrivateMenteeRow[]>([]);
  const [organisationRows, setOrganisationRows] = useState<OrganisationRow[]>([]);
  const [registryLoading, setRegistryLoading] = useState(true);
  const [generatorActiveTab, setGeneratorActiveTab] = useState<DemoKeyTab>('individual');
  const [individualDemoForm, setIndividualDemoForm] = useState({
    clientName: '',
    clientEmail: '',
  });
  const [organisationDemoForm, setOrganisationDemoForm] = useState({
    contactName: '',
    organisationName: '',
    contactEmail: '',
  });
  const [demoKeyLedgerRows, setDemoKeyLedgerRows] = useState<DemoKeyLedgerRow[]>([]);
  const [sendingDemoCode, setSendingDemoCode] = useState<string | null>(null);
  const [supportDocumentModal, setSupportDocumentModal] = useState<string | null>(null);
  const [privateIncidents, setPrivateIncidents] = useState<PrivateIncidentRow[]>([]);
  const [corporateSupportRows, setCorporateSupportRows] = useState<CorporateSupportRow[]>([]);
  const [incidentReviewId, setIncidentReviewId] = useState<string | null>(null);
  const [triageOrgKey, setTriageOrgKey] = useState<string | null>(null);

  useEffect(() => {
    if (gateOpen) return;
    if (masterSandboxUnlocked || user?.masterTestingOverride) {
      openMasterAdminGate();
      setGateOpen(true);
    }
  }, [gateOpen, masterSandboxUnlocked, user?.masterTestingOverride]);

  useEffect(() => {
    if (!gateOpen) return;

    let cancelled = false;

    Promise.allSettled([
      masterAdminApi.listPrivateMentees(),
      masterAdminApi.listOrganisations(),
      masterAdminApi.listDemoCodes(),
      masterAdminApi.listSupportTickets(),
    ])
      .then(([privateResult, orgResult, demoResult, ticketResult]) => {
        if (cancelled) return;
        if (privateResult.status === 'fulfilled') {
          setPrivateMenteeRows(privateResult.value.rows || []);
        }
        if (orgResult.status === 'fulfilled') {
          setOrganisationRows(orgResult.value.rows || []);
        }
        if (demoResult.status === 'fulfilled') {
          setDemoKeyLedgerRows((demoResult.value.rows || []).map(mapDemoCodeRow));
        }
        if (ticketResult.status === 'fulfilled') {
          setCorporateSupportRows(ticketResult.value.rows || []);
        }
      })
      .finally(() => {
        if (!cancelled) setRegistryLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [gateOpen]);

  const handleDeletePrivateMentee = async (menteeKey: string) => {
    if (!window.confirm(DELETE_CONFIRM_MESSAGE)) return;

    try {
      await masterAdminApi.deletePrivateMentee(menteeKey);
      setPrivateMenteeRows((rows) => rows.filter((row) => row.menteeKey !== menteeKey));
    } catch {
      window.alert('Unable to delete this entry.');
    }
  };

  const handleDeleteOrganisation = async (organisationKey: string) => {
    if (organisationKey === PROTECTED_SANDBOX_ORG_TOKEN) {
      window.alert('ELT-DEV-9842 is the Maintenance Preview sandbox and cannot be deleted.');
      return;
    }

    if (!window.confirm(DELETE_CONFIRM_MESSAGE)) return;

    try {
      await masterAdminApi.deleteOrganisation(organisationKey);
      setOrganisationRows((rows) => rows.filter((row) => row.organisationKey !== organisationKey));
    } catch (err: unknown) {
      const message = (err as { error?: string })?.error || 'Unable to delete this entry.';
      window.alert(message);
    }
  };

  const handleDeleteSupportTicket = async (row: CorporateSupportRow) => {
    const ticketId = row.ticketId;
    if (!ticketId) {
      window.alert('This support ticket cannot be deleted.');
      return;
    }
    if (!window.confirm('Delete this support ticket from System Data? This does not delete any email already sent.')) {
      return;
    }
    try {
      await masterAdminApi.deleteSupportTicket(ticketId);
      setCorporateSupportRows((rows) => rows.filter((item) => item.ticketId !== ticketId));
    } catch {
      window.alert('Unable to delete this support ticket.');
    }
  };

  const updateIndividualDemoForm = (field: keyof typeof individualDemoForm, value: string) => {
    setIndividualDemoForm((current) => ({ ...current, [field]: value }));
  };

  const updateOrganisationDemoForm = (field: keyof typeof organisationDemoForm, value: string) => {
    setOrganisationDemoForm((current) => ({ ...current, [field]: value }));
  };

  const generateDemoKey = async () => {
    const isIndividual = generatorActiveTab === 'individual';
    const record = isIndividual
      ? [individualDemoForm.clientName.trim(), individualDemoForm.clientEmail.trim()]
      : [
        organisationDemoForm.contactName.trim(),
        organisationDemoForm.organisationName.trim(),
        organisationDemoForm.contactEmail.trim(),
      ];
    const registeredEmail = isIndividual
      ? individualDemoForm.clientEmail.trim()
      : organisationDemoForm.contactEmail.trim();
    const registeredName = isIndividual
      ? individualDemoForm.clientName.trim()
      : organisationDemoForm.contactName.trim();

    if (!registeredEmail) {
      window.alert('Please enter a registered email before generating a demo key.');
      return;
    }

    try {
      const { row } = await masterAdminApi.createDemoCode({
        track: isIndividual ? 'PRO' : 'ELT',
        registeredEmail,
        registeredName: registeredName || undefined,
        ledgerRecord: record.filter(Boolean),
      });
      setDemoKeyLedgerRows((rows) => [...rows, mapDemoCodeRow(row)]);
      window.alert(
        `Demo key ${row.code} was created.\n\nNo email was sent.\n\nClick SEND TO CLIENT to open the message in your mailbox, edit it, then send from there.`,
      );
    } catch {
      const demoCode = mintDemoCreationCode(isIndividual ? 'PRO' : 'ELT');
      setDemoKeyLedgerRows((rows) => [
        ...rows,
        {
          id: `demo-${Date.now()}`,
          demoCode,
          type: isIndividual ? 'Individual' : 'Org',
          status: 'Not Used',
          registeredEmail: registeredEmail || '—',
          record,
        },
      ]);
    }
  };

  const deleteDemoKeyRow = async (rowId: string, demoCode: string) => {
    try {
      await masterAdminApi.deleteDemoCode(demoCode);
    } catch {
      /* local-only row */
    }
    setDemoKeyLedgerRows((rows) => rows.filter((row) => row.id !== rowId));
  };

  const sendDemoKeyEmail = async (row: DemoKeyLedgerRow) => {
    if (!row.registeredEmail || row.registeredEmail === '—') {
      window.alert('No registered email on file for this demo key.');
      return;
    }

    const draftWin = openMailDraftWindow();
    if (!draftWin) {
      window.alert('Allow pop-ups for this site so Gmail can open a draft. Then click SEND again.');
      return;
    }

    setSendingDemoCode(row.demoCode);
    try {
      const preview = await masterAdminApi.getDemoCodeEmailPreview(row.demoCode);
      const opened = await fillGmailDraft(draftWin, {
        to: String(preview.recipientEmail || row.registeredEmail || '').trim(),
        subject: String(preview.subject || 'Your Mentology Portal Access Key').trim(),
        body: preview.text,
      });
      window.alert(describeGmailDraftResult(opened));
    } catch {
      try { draftWin.close(); } catch { /* ignore */ }
      window.alert('Unable to open the onboarding email in Gmail Drafts.');
    } finally {
      setSendingDemoCode(null);
    }
  };

  const copyDemoKeyEmail = async (row: DemoKeyLedgerRow) => {
    try {
      const preview = await masterAdminApi.getDemoCodeEmailPreview(row.demoCode);
      const payload = [
        `To: ${preview.recipientEmail}`,
        `Subject: ${preview.subject}`,
        '',
        preview.text,
      ].join('\n');
      await navigator.clipboard.writeText(payload);
      window.alert('Onboarding email copied. Paste into your mail client and send to the client.');
    } catch {
      window.alert('Unable to copy onboarding email for this demo key.');
    }
  };

  const activeIncidentReview = privateIncidents.find((row) => row.id === incidentReviewId) ?? null;

  const resolveIncident = (incidentId: string) => {
    setPrivateIncidents((rows) =>
      rows.map((row) =>
        row.id === incidentId
          ? { ...row, resolved: true, incidentStatus: 'Resolved' }
          : row,
      ),
    );
    setIncidentReviewId(null);
  };

  const setCorporateSupportStatus = (organisationKey: string, status: SupportStatus) => {
    setCorporateSupportRows((rows) =>
      rows.map((row) => (row.organisationKey === organisationKey ? { ...row, status } : row)),
    );
    setTriageOrgKey(null);
  };

  const renderSystemData = () => (
    <div className="org-panel-card system-data-workspace">
      <h2 className="section-header system-data-heading">System Data</h2>
      <p className="system-data-intro">
        Master platform incident manager separating private emergency protocol alerts from corporate infrastructure support submissions.
      </p>

      <div className="system-data-stack">
        <div className="system-data-topic">
          <h3 className="system-data-topic-heading">
            Private Mentee Incidents (RED Email Protocols & System Errors)
          </h3>
          <div className="system-data-table-wrap">
            <table className="system-data-table">
              <thead>
                <tr>
                  <th>Mentee Key</th>
                  <th>Timestamp</th>
                  <th>Alert Source</th>
                  <th>Incident Status</th>
                  <th>System Response</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {privateIncidents.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="system-data-empty-row">
                      No private mentee incidents logged.
                    </td>
                  </tr>
                ) : (
                  privateIncidents.map((row) => (
                  <tr key={row.id}>
                    <td><span className="system-data-key">{row.menteeKey}</span></td>
                    <td>{row.timestamp}</td>
                    <td>{row.alertSource}</td>
                    <td>
                      <span
                        className={`system-data-incident-badge ${
                          row.resolved
                            ? 'system-data-incident-resolved'
                            : row.alertSource.includes('RED Email') || row.alertSource.includes('Red Phone')
                              ? 'system-data-incident-critical'
                              : 'system-data-incident-high'
                        }`}
                      >
                        {row.incidentStatus}
                      </span>
                    </td>
                    <td>{row.systemResponse}</td>
                    <td>
                      <button
                        type="button"
                        className="system-data-action-link"
                        onClick={() => setIncidentReviewId(row.id)}
                      >
                        [ Review Incident ]
                      </button>
                    </td>
                  </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="system-data-topic">
          <h3 className="system-data-topic-heading">
            Corporate Support Submissions (Mentoring Organisations)
          </h3>
          <div className="system-data-table-wrap">
            <table className="system-data-table system-data-table-corporate">
              <thead>
                <tr>
                  <th>Organisation Key</th>
                  <th>Organisation Name</th>
                  <th>Contact Name</th>
                  <th>Support File Link</th>
                  <th>Status</th>
                  <th>Admin Control</th>
                  <th>Delete</th>
                </tr>
              </thead>
              <tbody>
                {corporateSupportRows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="system-data-empty-row">
                      No corporate support submissions logged.
                    </td>
                  </tr>
                ) : (
                  corporateSupportRows.map((row) => (
                  <tr key={row.organisationKey}>
                    <td><span className="system-data-key">{row.organisationKey}</span></td>
                    <td>{row.organisationName}</td>
                    <td>{row.contactName}</td>
                    <td>
                      <button
                        type="button"
                        className="system-data-doc-link"
                        onClick={() => setSupportDocumentModal(row.supportDocument)}
                      >
                        [ View Support Document ]
                      </button>
                    </td>
                    <td>
                      <span
                        className={`system-data-support-status ${
                          row.status === 'Resolved'
                            ? 'system-data-support-resolved'
                            : row.status === 'In Progress'
                              ? 'system-data-support-progress'
                              : 'system-data-support-open'
                        }`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="system-data-action-link"
                        onClick={() => setTriageOrgKey(row.organisationKey)}
                      >
                        [ Triage Status ]
                      </button>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="system-data-action-link"
                        onClick={() => void handleDeleteSupportTicket(row)}
                      >
                        DELETE
                      </button>
                    </td>
                  </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {activeIncidentReview && (
        <div
          className="system-data-modal-overlay"
          role="presentation"
          onClick={() => setIncidentReviewId(null)}
        >
          <div
            className="system-data-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="system-data-incident-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 id="system-data-incident-modal-title" className="system-data-modal-title">
              Incident Telemetry — {activeIncidentReview.menteeKey}
            </h4>
            <pre className="system-data-modal-body">{activeIncidentReview.telemetry}</pre>
            {!activeIncidentReview.resolved && (
              <button
                type="button"
                className="btn-primary system-data-modal-action"
                onClick={() => resolveIncident(activeIncidentReview.id)}
              >
                [ Resolve & Clear Incident ]
              </button>
            )}
            <button type="button" className="btn-primary system-data-modal-close" onClick={() => setIncidentReviewId(null)}>
              Close
            </button>
          </div>
        </div>
      )}

      {triageOrgKey && (
        <div
          className="system-data-modal-overlay"
          role="presentation"
          onClick={() => setTriageOrgKey(null)}
        >
          <div
            className="system-data-modal system-data-triage-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="system-data-triage-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 id="system-data-triage-title" className="system-data-modal-title">Triage Status</h4>
            <p className="system-data-triage-subtext">
              {corporateSupportRows.find((row) => row.organisationKey === triageOrgKey)?.organisationName}
            </p>
            <div className="system-data-triage-options">
              {SUPPORT_STATUS_OPTIONS.map((status) => (
                <button
                  key={status}
                  type="button"
                  className={`system-data-triage-option ${
                    corporateSupportRows.find((row) => row.organisationKey === triageOrgKey)?.status === status
                      ? 'system-data-triage-option-active'
                      : ''
                  }`}
                  onClick={() => setCorporateSupportStatus(triageOrgKey, status)}
                >
                  {status}
                </button>
              ))}
            </div>
            <button type="button" className="btn-primary system-data-modal-close" onClick={() => setTriageOrgKey(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {supportDocumentModal && (
        <div
          className="system-data-modal-overlay"
          role="presentation"
          onClick={() => setSupportDocumentModal(null)}
        >
          <div
            className="system-data-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="system-data-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 id="system-data-modal-title" className="system-data-modal-title">Support Document</h4>
            <pre className="system-data-modal-body">{supportDocumentModal}</pre>
            <button type="button" className="btn-primary system-data-modal-close" onClick={() => setSupportDocumentModal(null)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const renderDemoKeyGenerator = () => (
    <div className="org-panel-card demo-key-workspace">
      <h2 className="section-header demo-key-heading">Demo Key Generator</h2>
      <p className="demo-key-intro">
        Mint sandbox validation codes in the Enter Demo Key format (PRO-XXX / ELT-XXX) to rapidly onboard preview clients and individual test nodes.
        SEND TO CLIENT opens the message in your mailbox so you can edit it, then send. COPY EMAIL copies the draft.
      </p>

      <div className="demo-key-creation-panel">
        <form className="demo-key-form" onSubmit={(e) => e.preventDefault()} autoComplete="off">
          <div className="demo-key-entry-field demo-key-generator-format">
            <p className="demo-key-entry-label">Enter Demo Key</p>
            <p className="demo-key-entry-format">(PRO-XXX / ELT-XXX)</p>
          </div>
          <div className="demo-key-columns" role="group" aria-label="Demo key client type">
            <div
              className={`demo-key-column ${generatorActiveTab === 'individual' ? 'demo-key-column-active' : ''}`}
            >
              <button
                type="button"
                aria-pressed={generatorActiveTab === 'individual'}
                className={`demo-key-column-heading ${generatorActiveTab === 'individual' ? 'demo-key-column-heading-active' : ''}`}
                onClick={() => setGeneratorActiveTab('individual')}
              >
                Individual / Private
              </button>
              <div className="demo-key-field-stack">
                <input
                  className="input-field demo-key-input"
                  type="text"
                  name="demo-individual-client-name"
                  autoComplete="off"
                  placeholder="Enter Client Name"
                  value={individualDemoForm.clientName}
                  onChange={(e) => updateIndividualDemoForm('clientName', e.target.value)}
                  disabled={generatorActiveTab !== 'individual'}
                />
                <input
                  className="input-field demo-key-input"
                  type="email"
                  name="demo-individual-client-email"
                  autoComplete="off"
                  placeholder="Enter Client Email"
                  value={individualDemoForm.clientEmail}
                  onChange={(e) => updateIndividualDemoForm('clientEmail', e.target.value)}
                  disabled={generatorActiveTab !== 'individual'}
                />
              </div>
            </div>

            <div
              className={`demo-key-column ${generatorActiveTab === 'organisation' ? 'demo-key-column-active' : ''}`}
            >
              <button
                type="button"
                aria-pressed={generatorActiveTab === 'organisation'}
                className={`demo-key-column-heading ${generatorActiveTab === 'organisation' ? 'demo-key-column-heading-active' : ''}`}
                onClick={() => setGeneratorActiveTab('organisation')}
              >
                Organisation
              </button>
              <div className="demo-key-field-stack">
                <input
                  className="input-field demo-key-input"
                  type="text"
                  name="demo-org-contact-name"
                  autoComplete="off"
                  placeholder="Enter Contact Name"
                  value={organisationDemoForm.contactName}
                  onChange={(e) => updateOrganisationDemoForm('contactName', e.target.value)}
                  disabled={generatorActiveTab !== 'organisation'}
                />
                <input
                  className="input-field demo-key-input"
                  type="text"
                  name="demo-org-organisation-name"
                  autoComplete="off"
                  placeholder="Enter Organisation Name"
                  value={organisationDemoForm.organisationName}
                  onChange={(e) => updateOrganisationDemoForm('organisationName', e.target.value)}
                  disabled={generatorActiveTab !== 'organisation'}
                />
                <input
                  className="input-field demo-key-input"
                  type="email"
                  name="demo-org-contact-email"
                  autoComplete="off"
                  placeholder="Enter Contact Email"
                  value={organisationDemoForm.contactEmail}
                  onChange={(e) => updateOrganisationDemoForm('contactEmail', e.target.value)}
                  disabled={generatorActiveTab !== 'organisation'}
                />
              </div>
            </div>
          </div>

          <button type="button" className="btn-primary demo-key-action-btn" onClick={generateDemoKey}>
            [ + Generate Enter Demo Key ]
          </button>
        </form>
      </div>

      <div className="demo-key-ledger-section">
        <h3 className="demo-key-ledger-heading">Control Line Ledger</h3>
        <div className="demo-key-ledger-wrap">
          <table className="demo-key-ledger-table">
            <thead>
              <tr>
                <th>Enter Demo Key</th>
                <th>Type: Org/Individual</th>
                <th>Status: Active/Not Used</th>
                <th>Registered Email</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {demoKeyLedgerRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="demo-key-ledger-empty">
                    No demo codes generated yet.
                  </td>
                </tr>
              ) : (
                demoKeyLedgerRows.map((row) => (
                  <tr key={row.id}>
                    <td><span className="demo-key-ledger-code">{row.demoCode}</span></td>
                    <td>{row.type}</td>
                    <td>
                      <span className={`demo-key-ledger-status demo-key-ledger-status-${row.status === 'Active' ? 'active' : 'unused'}`}>
                        {row.status}
                      </span>
                    </td>
                    <td>{row.registeredEmail}</td>
                    <td>
                      <div className="demo-key-ledger-actions">
                        <button
                          type="button"
                          className="demo-key-send-btn"
                          disabled={sendingDemoCode === row.demoCode || row.registeredEmail === '—'}
                          onClick={() => sendDemoKeyEmail(row)}
                        >
                          {sendingDemoCode === row.demoCode ? 'OPENING...' : 'SEND TO CLIENT'}
                        </button>
                        <button
                          type="button"
                          className="demo-key-copy-btn"
                          onClick={() => copyDemoKeyEmail(row)}
                        >
                          COPY EMAIL
                        </button>
                        <button
                          type="button"
                          className="demo-key-delete-btn"
                          onClick={() => deleteDemoKeyRow(row.id, row.demoCode)}
                        >
                          DELETE
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderWindowA = () => (
    <div className="org-panel-card window-a-workspace">
      <h2 className="section-header window-a-heading">Window A — Private Mentees</h2>
      <p className="window-a-intro">
        Dynamically tracking direct global registrants, individual platform subscriptions, and automated system nodes.
      </p>
      <div className="window-a-table-wrap">
        <table className="window-a-table">
          <thead>
            <tr>
              <th>Mentee Key</th>
              <th>Date Enrolled</th>
              <th>Mentee Name</th>
              <th>Contact Info</th>
              <th>Location Address</th>
              <th>Subscription State</th>
              <th>Time Left</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {registryLoading ? (
              <tr>
                <td colSpan={8} className="window-empty-row">
                  Loading private mentee registry...
                </td>
              </tr>
            ) : privateMenteeRows.length === 0 ? (
              <tr>
                <td colSpan={8} className="window-empty-row">
                  No private mentees registered.
                </td>
              </tr>
            ) : (
              privateMenteeRows.map((row) => (
              <tr key={row.menteeKey}>
                <td><span className="window-a-key">{row.menteeKey}</span></td>
                <td>{row.dateEnrolled}</td>
                <td>{row.menteeName}</td>
                <td>
                  <div className="window-a-contact-stack">
                    <span>{row.phone}</span>
                    <span>{row.email}</span>
                  </div>
                </td>
                <td>{row.locationAddress}</td>
                <td>
                  <span className={`window-a-status window-a-status-${row.subscriptionState.replace(/\s+/g, '-').toLowerCase()}`}>
                    {row.subscriptionState}
                  </span>
                </td>
                <td>
                  <span className={`window-a-time-left ${row.subscriptionState === 'Lapsed' ? 'window-a-time-lapsed' : ''}`}>
                    {row.timeLeft}
                  </span>
                </td>
                <td>
                  <button
                    type="button"
                    className="window-action-delete-btn"
                    onClick={() => handleDeletePrivateMentee(row.menteeKey)}
                  >
                    DELETE
                  </button>
                </td>
              </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderWindowB = () => (
    <div className="org-panel-card window-b-workspace">
      <h2 className="section-header window-b-heading">Window B — Mentoring Organisations</h2>
      <p className="window-b-intro">
        Master corporate tenant control panel tracking deployment states, contract lifetimes, and primary enterprise liaisons.
      </p>
      <div className="window-b-table-wrap">
        <table className="window-b-table">
          <thead>
            <tr>
              <th>Organisation Key</th>
              <th>Date Enrolled</th>
              <th>Organisation Name</th>
              <th>Contact Name</th>
              <th>Contact Info</th>
              <th>Subscription State</th>
              <th>Time Left</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {registryLoading ? (
              <tr>
                <td colSpan={8} className="window-empty-row">
                  Loading organisation registry...
                </td>
              </tr>
            ) : organisationRows.length === 0 ? (
              <tr>
                <td colSpan={8} className="window-empty-row">
                  No mentoring organisations registered.
                </td>
              </tr>
            ) : (
              organisationRows.map((row) => (
              <tr key={row.organisationKey}>
                <td><span className="window-b-key">{row.organisationKey}</span></td>
                <td>{row.dateEnrolled}</td>
                <td>{row.organisationName}</td>
                <td>{row.contactName}</td>
                <td>
                  <div className="window-b-contact-stack">
                    <span>{row.phone}</span>
                    <span>{row.email}</span>
                  </div>
                </td>
                <td>
                  <span className={`window-b-status window-b-status-${row.subscriptionState.replace(/\s+/g, '-').toLowerCase()}`}>
                    {row.subscriptionState}
                  </span>
                </td>
                <td>
                  <span className={`window-b-time-left ${row.subscriptionState === 'Lapsed' ? 'window-b-time-lapsed' : ''}`}>
                    {row.timeLeft}
                  </span>
                </td>
                <td>
                  <button
                    type="button"
                    className="window-action-delete-btn"
                    disabled={row.organisationKey === PROTECTED_SANDBOX_ORG_TOKEN}
                    title={
                      row.organisationKey === PROTECTED_SANDBOX_ORG_TOKEN
                        ? 'ELT-DEV-9842 is the Maintenance Preview sandbox and cannot be deleted.'
                        : undefined
                    }
                    onClick={() => handleDeleteOrganisation(row.organisationKey)}
                  >
                    DELETE
                  </button>
                </td>
              </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderKnowledgeHub = () => (
    <div className="org-panel-card knowledge-hub-workspace">
      <h2 className="section-header knowledge-hub-heading">Global Knowledge Hub</h2>
      <p className="knowledge-hub-intro">
        Manage and cross-reference platform core intelligence, research repositories, and operational packages.
      </p>

      <div className="knowledge-hub-tabs" role="tablist" aria-label="Knowledge Hub views">
        <button
          type="button"
          role="tab"
          aria-selected={knowledgeHubTab === 'baselines'}
          className={`knowledge-hub-tab ${knowledgeHubTab === 'baselines' ? 'active' : ''}`}
          onClick={() => setKnowledgeHubTab('baselines')}
        >
          Knowledge Engine Baselines
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={knowledgeHubTab === 'operational'}
          className={`knowledge-hub-tab ${knowledgeHubTab === 'operational' ? 'active' : ''}`}
          onClick={() => setKnowledgeHubTab('operational')}
        >
          Operational Systems Pack
        </button>
      </div>

      {knowledgeHubTab === 'baselines' ? (
        <div className="knowledge-hub-panel" role="tabpanel">
          <h3 className="knowledge-hub-panel-heading">Knowledge Engine Baselines</h3>
          <p className="knowledge-hub-panel-subtext">
            Global research, theoretical frameworks, and academic knowledge repositories.
          </p>
          <table className="knowledge-hub-table">
            <thead>
              <tr>
                <th>Folder Name</th>
                <th>Internal Context Map</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {KNOWLEDGE_BASELINES.map((row) => (
                <tr key={row.folderName}>
                  <td>{row.folderName}</td>
                  <td>{row.contextMap}</td>
                  <td>
                    <span className="knowledge-hub-status">{row.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="knowledge-hub-panel" role="tabpanel">
          <h3 className="knowledge-hub-panel-heading">Operational Systems Pack</h3>
          <p className="knowledge-hub-panel-subtext">
            Platform delivery architectures, system reference packages, and deployment dynamics.
          </p>
          <div className="knowledge-hub-topics">
            {OPERATIONAL_TOPICS.map((topic) => (
              <div key={topic.title} className="knowledge-hub-topic">
                <h4 className="knowledge-hub-topic-title">{topic.title}</h4>
                <ul className="knowledge-hub-folder-list">
                  {topic.folders.map((folder) => (
                    <li key={folder}>📁 {folder}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderPanel = () => {
    if (activeSection === 'Window A (Private)') {
      return renderWindowA();
    }
    if (activeSection === 'Window B (Org)') {
      return renderWindowB();
    }
    if (activeSection === 'Demo Key Generator') {
      return renderDemoKeyGenerator();
    }
    if (activeSection === 'System Data') {
      return renderSystemData();
    }
    if (activeSection === 'Maintenance Preview') {
      return <MasterAdminMaintenancePreview />;
    }
    return renderKnowledgeHub();
  };

  const unlockMasterAdmin = async (key: string) => {
    const data = await login(key);
    if (data.masterTestingOverride || data.user?.masterTestingOverride) {
      openMasterAdminGate();
      setGateOpen(true);
    }
    return data;
  };

  if (!gateOpen) {
    return <MasterAdminLock onUnlock={unlockMasterAdmin} onReject={logout} />;
  }

  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        <img
          src={encodeURI('/Assets/logo/Mentology Logo.png')}
          alt="Mentology"
          className="master-sidebar-logo brand-logo"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
        <p className="master-admin-sidebar-heading">Master Admin Control</p>
        {MASTER_ADMIN_SIDEBAR_NAV.map((item) => {
          if (item.kind === 'external') {
            return (
              <a
                key={item.label}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="admin-nav-item admin-nav-external-link"
              >
                {item.label}
              </a>
            );
          }

          return (
            <button
              key={item.label}
              type="button"
              className={`admin-nav-item ${activeSection === item.label ? 'active' : ''}`}
              onClick={() => setActiveSection(item.label)}
            >
              {item.label}
            </button>
          );
        })}
      </aside>
      <main className={`admin-panel${activeSection === 'Maintenance Preview' ? ' admin-panel-maintenance' : ''}`}>
        {renderPanel()}
      </main>
    </div>
  );
}
